import type { CustomerOrder } from '../../customerOrdersApi'

export type PaymentMethodVisualBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'generic'

export type PaymentMethodVisualModel = {
  label: string
  displayLine: string
  brand: PaymentMethodVisualBrand
}

const resolvePaymentBrandFromText = (value: string | null): PaymentMethodVisualBrand => {
  const normalized = value?.trim().toLowerCase() ?? ''
  if (normalized.includes('visa')) return 'visa'
  if (normalized.includes('mastercard') || normalized.includes('master card')) return 'mastercard'
  if (normalized.includes('american express') || normalized === 'amex' || normalized.includes('amex')) return 'amex'
  if (normalized.includes('discover')) return 'discover'
  return 'generic'
}

const parseBrandAndLast4FromLabel = (label: string): { brand: string | null; last4: string | null } => {
  const parsed = label.match(/^(.*?)\s*••••\s*([0-9]{4})$/u)
  if (!parsed) return { brand: null, last4: null }
  const brand = parsed[1]?.trim() || null
  const last4 = parsed[2]?.trim() || null
  return { brand, last4 }
}

export const resolvePaymentMethodVisualModel = (
  orderDetail: CustomerOrder,
  fallbackLabel: string,
): PaymentMethodVisualModel => {
  const label = orderDetail.paymentMethodLabel?.trim() || fallbackLabel
  const parsed = parseBrandAndLast4FromLabel(label)
  const brandText = orderDetail.paymentMethodBrand?.trim() || parsed.brand
  const last4 = orderDetail.paymentMethodLast4?.trim() || parsed.last4

  if (brandText && last4) {
    return {
      label,
      displayLine: `${brandText} •••• ${last4}`,
      brand: resolvePaymentBrandFromText(brandText),
    }
  }

  return {
    label,
    displayLine: label,
    brand: resolvePaymentBrandFromText(orderDetail.paymentMethodBrand?.trim() || parsed.brand),
  }
}

export const PaymentMethodLogo = ({ brand }: { brand: PaymentMethodVisualBrand }) => {
  if (brand === 'visa') {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center text-[10px] font-bold tracking-[0.08em] text-[#1a4dff]">
        VISA
      </span>
    )
  }

  if (brand === 'mastercard') {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center">
        <svg viewBox="0 0 28 18" className="h-4.5 w-6.5" aria-hidden>
          <circle cx="10" cy="9" r="6" fill="#eb001b" />
          <circle cx="18" cy="9" r="6" fill="#f79e1b" fillOpacity="0.9" />
        </svg>
      </span>
    )
  }

  if (brand === 'amex') {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center text-[9px] font-bold tracking-[0.07em] text-[#0f6ed7]">
        AMEX
      </span>
    )
  }

  if (brand === 'discover') {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center text-[8.5px] font-bold tracking-[0.06em] text-[#db6f00]">
        DISC
      </span>
    )
  }

  return (
    <span className="inline-flex h-8 w-8 items-center justify-center text-(--color-text-secondary)">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
        <rect x="3.5" y="6.5" width="17" height="11" rx="2" />
        <path d="M3.5 10h17" />
      </svg>
    </span>
  )
}
